import type {
  ForgetPasswordOTPRequest,
  SelectableCountry,
  SignUpRequest,
} from '@/types';

import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import CancelButton from '../CancelButton';

import { Button, Flex, MobileInput, Stack, Text } from '@/components';
import {
  useForgetPasswordOtp,
  useInputAttributes,
  useTranslation,
} from '@/hooks';
import { englishOnlyParams, mobileRegex, yupValidator } from '@/utils';

type MobileFormData = Pick<ForgetPasswordOTPRequest, 'mobile'>;

type MobileFormProps = {
  goNext: (data: ForgetPasswordOTPRequest) => void;
};

const MobileForm: React.FC<MobileFormProps> = ({ goNext }) => {
  const { t } = useTranslation('pages.forgetPassword.form.mobileForm');

  const [selectedCountry, setSelectedCountry] =
    React.useState<SelectableCountry>('iran');

  const countryCodes: Record<
    SelectableCountry,
    SignUpRequest['country_phone_prefix']
  > = {
    iran: '+98',
    iraq: '+964',
    afghanistan: '+93',
  };

  const validationSchema = yupValidator.object().shape({
    mobile: yupValidator
      .string()
      .test({ ...englishOnlyParams })
      .matches(mobileRegex[selectedCountry], t('invalidNumber'))
      .label(t('mobile'))
      .required(),
  });

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const filedInputProps = useInputAttributes<MobileFormData>(
    control,
    register,
    errors,
    {
      mobile: t('mobile'),
    }
  );
  const { mutateAsync: getOtp, isPending: getOtpLoading } =
    useForgetPasswordOtp();

  const onSubmit = (data: MobileFormData) => {
    const payload = {
      ...data,
      country_code_prefix: countryCodes[selectedCountry],
    };
    void getOtp(payload).then(() => {
      goNext(payload);
    });
  };

  return (
    <Stack flex={1} w="full" h="100%" gap={0} as="form">
      <Text
        textAlign="center"
        fontSize={{ base: 'md', md: 'xl' }}
        w="full"
        fontWeight="medium"
      >
        {t('forgetPasswordHint')}
      </Text>
      <Flex flex={1} gap={4} mt={{ base: '50px', md: '80px' }}>
        <MobileInput
          inputProps={{ ...filedInputProps.mobile }}
          selectedCountry={selectedCountry}
          setSelectedCountry={setSelectedCountry}
        />
      </Flex>
      <Stack w="full" align="center" gap={6} justify="end" flexGrow={1}>
        <Button
          w="full"
          type="submit"
          onClick={(data) => void handleSubmit(onSubmit)(data)}
          isLoading={getOtpLoading}
        >
          {t('sendCode')}
        </Button>
        <CancelButton />
      </Stack>
    </Stack>
  );
};

export default MobileForm;
